import momentTimezone from 'moment-timezone';
import React, {memo, useContext, useMemo} from 'react';
import {useIntl} from 'react-intl';
import {ThemeContext} from '../../apiContexts';
import LoadingBar from '../../components/loadingBar';
import ReactTable from '../../components/reactTable';
import {convertDistanceTo, convertSpeedTo, convertVolumeTo, nomenclatureDistance, nomenclatureSpeedOf, nomenclatureVolume} from '../../helpers/conversions';
import {durationFormat} from '../../helpers/date';
import {getColumnWidth} from '../../helpers/report';
import ReplayControls from './replay-controls';
import {Header, ListStyle} from './styles';
import SelectDeviceButton from './selectDeviceButton';

const TripList = ({
  className,
  data,
  timezone,
  onSelect,
  speedUnit,
  distanceUnit,
  volumeUnit,
  loading,
  ...props
}) => {
  const theme = useContext(ThemeContext);
  const intl = useIntl();

  const columns = useMemo(
      () => [
        {
          Header: intl.formatMessage({id: 'sharedName'}),
          accessor: 'deviceName',
          minWidth: 200,
          width: getColumnWidth(data, 'deviceName', intl.formatMessage({id: 'sharedName'})),
        },
        {
          Header: intl.formatMessage({id: 'reportStartTime'}),
          accessor: 'startTime',
          Cell: ({cell: {value}}) =>
            momentTimezone(value)
                .tz(timezone)
                .format(intl.formatMessage({id: 'dateTimeLongFormat'})),
          minWidth: 200,
        },
        {
          Header: intl.formatMessage({id: 'reportStartOdometer'}),
          accessor: 'startOdometer',
          minWidth: 200,
          Cell: ({cell: {value}}) =>
            `${Number(convertDistanceTo(value, distanceUnit)).toFixed(2) ||
            0} ${nomenclatureDistance(distanceUnit)}`,
        },
        {
          Header: intl.formatMessage({id: 'reportStartAddress'}),
          accessor: 'startAddress',
          Cell: ({cell: {value}}) => value || '-',
          minWidth: 150,
          width: getColumnWidth(data, 'startAddress', intl.formatMessage({id: 'reportStartAddress'})),
        },
        {
          Header: intl.formatMessage({id: 'reportEndTime'}),
          accessor: 'endTime',
          Cell: ({cell: {value}}) =>
            momentTimezone(value)
                .tz(timezone)
                .format(intl.formatMessage({id: 'dateTimeLongFormat'})),
          minWidth: 200,
        },
        {
          Header: intl.formatMessage({id: 'reportEndOdometer'}),
          accessor: 'endOdometer',
          minWidth: 200,
          Cell: ({cell: {value}}) =>
            `${Number(convertDistanceTo(value, distanceUnit)).toFixed(2) ||
            0} ${nomenclatureDistance(distanceUnit)}`,
        },
        {
          Header: intl.formatMessage({id: 'reportEndAddress'}),
          accessor: 'endAddress',
          Cell: ({cell: {value}}) => value || '-',
          minWidth: 150,
          width: getColumnWidth(data, 'endAddress', intl.formatMessage({id: 'reportEndAddress'})),
        },
        {
          Header: intl.formatMessage({id: 'sharedDistance'}),
          accessor: 'distance',
          minWidth: 200,
          Cell: ({cell: {value}}) =>
            `${Number(convertDistanceTo(value, distanceUnit)).toFixed(2) ||
            0} ${nomenclatureDistance(distanceUnit)}`,
        },
        {
          Header: intl.formatMessage({id: 'reportAverageSpeed'}),
          accessor: 'averageSpeed',
          minWidth: 200,
          Cell: ({cell: {value}}) =>
            `${Number(convertSpeedTo(value, speedUnit)).toFixed(1) ||
            0} ${nomenclatureSpeedOf(speedUnit)}`,
        },
        {
          Header: intl.formatMessage({id: 'reportMaximumSpeed'}),
          accessor: 'maxSpeed',
          minWidth: 200,
          Cell: ({cell: {value}}) =>
            `${Number(convertSpeedTo(value, speedUnit)).toFixed(1) ||
            0} ${nomenclatureSpeedOf(speedUnit)}`,
        },
        {
          Header: intl.formatMessage({id: 'reportDuration'}),
          accessor: 'duration',
          Cell: ({cell: {value}}) => durationFormat(value, 'H [h] mm [m]'),
        },
        {
          Header: intl.formatMessage({id: 'reportSpentFuel'}),
          accessor: 'spentFuel',
          minWidth: 200,
          Cell: ({cell: {value}}) =>
            `${Number(convertVolumeTo(value, volumeUnit)).toFixed(1) ||
            0} ${nomenclatureVolume(volumeUnit)}`,
        },
        {
          Header: intl.formatMessage({id: 'sharedDriver'}),
          accessor: 'driverUniqueId',
          minWidth: 150,
          width: getColumnWidth(data, 'driverUniqueId', intl.formatMessage({id: 'sharedDriver'})),
        },
      ],
      [data, distanceUnit, speedUnit, timezone, volumeUnit],
  );

  const selectItem = (props) => onSelect(props);

  return (
    <ListStyle theme={theme} className="custom-shadow-2">
      <Header>
        <h5>{intl.formatMessage({id: 'tripHistory'})}</h5>
        <ReplayControls />
        <SelectDeviceButton />
      </Header>
      {loading ? (
        <LoadingBar />
      ) : (
        <ReactTable data={data} columns={columns} selectItem={selectItem} />
      )}
    </ListStyle>
  );
};

export default memo(TripList);
